import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import IconMC from "../../../components/IconMC";

/**
 * Hook de UI para ChatModeDropdownMC
 * Separa la lógica de opciones, handlers y memoización.
 * No contiene lógica de datos ni de negocio externo.
 *
 * @param {string} selectedChatMode
 * @param {function} handleToggleChatMode
 * @returns {{ chatModeOptions: Array, selectedLabel: string }}
 */
export default function useChatModeDropdownUI(selectedChatMode, handleToggleChatMode) {
  const { t } = useTranslation('InboxMC');

  // Handlers memoizados
  const handleSelectChat = useCallback(() => handleToggleChatMode('chat'), [handleToggleChatMode]);
  const handleSelectNotes = useCallback(() => handleToggleChatMode('notes'), [handleToggleChatMode]);

  // Renderizado de contenido de opciones (única función memoizada)
  const renderContent = useCallback((type) => {
    const iconName = type === 'chat' ? 'message-circle-outline' : 'note-outline';
    const label = type === 'chat' ? t('chat_mode.chat') : t('chat_mode.notes');
    return (
      <div className="tw-flex tw-items-center">
        <IconMC size="base" name={iconName} className="tw-mr-1" />
        <span>{label}</span>
      </div>
    );
  }, [t]);

  // Opciones memoizadas con traducción y render callback único
  const chatModeOptions = useMemo(() => [
    {
      label: t('chat_mode.chat'),
      value: 'chat',
      onClick: handleSelectChat,
      iconPosition: 'right',
      icon: selectedChatMode === 'chat' ? 'checkmark-circle-2-fill' : '',
      className: '!tw-justify-between',
      iconClassName: '!tw-size-[22px] !tw-text-[#3CAAE1]',
      Content: () => renderContent('chat')
    },
    {
      label: t('chat_mode.notes'),
      value: 'notes',
      onClick: handleSelectNotes,
      iconPosition: 'right',
      icon: selectedChatMode === 'notes' ? 'checkmark-circle-2-fill' : '',
      className: '!tw-justify-between',
      iconClassName: '!tw-size-[22px] !tw-text-[#3CAAE1]',
      Content: () => renderContent('notes')
    }
  ], [selectedChatMode, handleSelectChat, handleSelectNotes, t, renderContent]);

  // Label seleccionado memoizado
  const selectedLabelChatMode = useMemo(() => {
    const found = chatModeOptions.find(option => option.value === selectedChatMode);
    return found ? found.label : '';
  }, [chatModeOptions, selectedChatMode]);

  return { chatModeOptions, selectedLabelChatMode };
}
